import React from "react";

// region External libraries
import { Button } from "@libraries/mui/components";
// endregion External libraries
// region Languages
import useTranslation from "@languages/useTranslation";
// endregion Languages

import { LegacyMigrationMessages } from "../languages/interfaces";
import * as Styled from "../styles";
import { MigrationProgress } from "../useLegacyMigration";

interface IMigratingProps {
  tenantName: string;
  progress: MigrationProgress;
  stopping: boolean;
  onStop: () => void;
}

/**
 * Step 4 — the users are being provisioned, a batch per request, from this
 * screen.
 *
 * Closing it stops the migration where it is, which the hint says out loud:
 * nothing carries on in the background, and what was already migrated stays.
 */
const Migrating: React.FC<IMigratingProps> = ({ tenantName, progress, stopping, onStop }) => {

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks

  const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <Styled.Card>
      <Styled.Centered>
        <Styled.StepTitle>
          {t(LegacyMigrationMessages.legacyMigrationMigratingTitle, { tenant: tenantName })}
        </Styled.StepTitle>

        <Styled.Progress $percent={percent}>
          <div />
        </Styled.Progress>

        <Styled.ProgressLabel>
          {t(LegacyMigrationMessages.legacyMigrationProgress, { done: progress.done, count: progress.total })}
        </Styled.ProgressLabel>

        <Styled.StepHint>{t(LegacyMigrationMessages.legacyMigrationMigratingHint)}</Styled.StepHint>

        {progress.errors > 0 && (
          <Styled.Badge $tone="bad">
            {t(LegacyMigrationMessages.legacyMigrationErrorsSoFar, { count: progress.errors })}
          </Styled.Badge>
        )}

        {/* The stop takes effect between batches: the one in flight finishes,
            so no user is left half-provisioned. */}
        <Button size="small" color="error" disabled={stopping} onClick={onStop}>
          {t(stopping
            ? LegacyMigrationMessages.legacyMigrationStopping
            : LegacyMigrationMessages.legacyMigrationStop)}
        </Button>
      </Styled.Centered>
    </Styled.Card>
  );
};

export default Migrating;
